import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import type { VirtualTreeNode, VirtualTreeNodeId, VirtualTreeSelectionChangeDetails } from '@scmlatam/web-ui';

import { employeeService } from '../services/service';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';
import type { IUseFormAssignHiringProps, Row } from '../types/hooks';
import type { AllTree, AllTreeArea, ResponseOrgpathEmployee } from '../types/services';
import { getRepeatedPositionRowIds } from './positionValidation';

export interface PositionNodeValue {
    nodeId: string;
    workPositionId: number;
    workPositionName: string;
    subcompanyId: number;
    subcompanyName: string;
    areaIds: number[];
    areaPathNames: string[];
}

export interface PositionRow {
    local_id: string;
    hiring_orgpath_id?: number | null;
    to_delete: boolean;
    subcompany_id: number;
    subcompany_name: string;
    area_ids: number[];
    area_path_names: string[];
    work_position_id: number;
    work_position_name: string;
    start_date: Dayjs | null;
    end_date: Dayjs | null;
    editingDates?: boolean;
}

type Props = Pick<IUseFormAssignHiringProps, 'rows' | 'rowId' | 'reload' | 'option' | 'setOption'>;

// =========================================================================
// Helpers puros
// =========================================================================

const genLocalId = () => `pr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const parseDateSafe = (value?: string | null): Dayjs | null => {
    if (!value) return null;
    const parsed = dayjs(value);
    if (parsed.isValid()) return parsed;
    const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!ddmmyyyy) return null;
    const [, d, m, y] = ddmmyyyy;
    const normalized = dayjs(`${y}-${m}-${d}`);
    return normalized.isValid() ? normalized : null;
};

interface DateInterval {
    start: Dayjs;
    end: Dayjs;
}

const mergeIntervals = (intervals: DateInterval[]): DateInterval[] => {
    const sorted = [...intervals].sort((a, b) => a.start.diff(b.start));
    const merged: DateInterval[] = [];

    for (const interval of sorted) {
        const last = merged[merged.length - 1];
        if (last && !interval.start.isAfter(last.end.add(1, 'day'), 'day')) {
            if (interval.end.isAfter(last.end, 'day')) last.end = interval.end;
        } else {
            merged.push({ ...interval });
        }
    }

    return merged;
};

// Calcula los huecos sin cobertura dentro del rango del contrato a partir de los intervalos ya asignados.
const computeContractGaps = (merged: DateInterval[], contractStart: Dayjs, contractEnd: Dayjs): DateInterval[] => {
    const gaps: DateInterval[] = [];
    let cursor = contractStart;

    for (const interval of merged) {
        if (interval.start.isAfter(cursor, 'day')) {
            gaps.push({ start: cursor, end: interval.start.subtract(1, 'day') });
        }
        if (interval.end.isAfter(cursor, 'day')) cursor = interval.end;
    }

    if (cursor.isBefore(contractEnd, 'day')) {
        gaps.push({ start: cursor.add(1, 'day'), end: contractEnd });
    }

    return gaps;
};

const buildPositionTree = (allTree: AllTree[]): VirtualTreeNode<PositionNodeValue>[] => {
    if (!allTree || !Array.isArray(allTree)) return [];

    return allTree.map((subcompany, subcompanyIdx) => {
        const subcompanyNodeId = `subcompany-${subcompany.subcompany_id}-${subcompanyIdx}`;

        const buildAreaNodes = (areas: AllTreeArea[], parentId: string, parentAreaIds: number[] = [], parentAreaNames: string[] = []): VirtualTreeNode<PositionNodeValue>[] => {
            if (!areas || !Array.isArray(areas)) return [];

            return areas.map((area, areaIdx) => {
                const currentAreaIds = [...parentAreaIds, area.area_id];
                const currentAreaNames = [...parentAreaNames, area.area_name];
                const areaNodeId = `${parentId}-area-${area.area_id}-${areaIdx}`;

                const subAreaNodes = buildAreaNodes(area.sub_areas ?? [], areaNodeId, currentAreaIds, currentAreaNames);

                const workPositionNodes: VirtualTreeNode<PositionNodeValue>[] = (area.work_positions ?? []).map((wp, wpIdx) => {
                    const wpNodeId = `${areaNodeId}-wp-${wp.work_position_id}-${wpIdx}`;
                    return {
                        id: wpNodeId,
                        label: wp.work_position_name,
                        type: 'work_position',
                        selectable: true,
                        value: {
                            nodeId: wpNodeId,
                            workPositionId: wp.work_position_id,
                            workPositionName: wp.work_position_name,
                            subcompanyId: subcompany.subcompany_id,
                            subcompanyName: subcompany.subcompany_name,
                            areaIds: currentAreaIds,
                            areaPathNames: currentAreaNames,
                        },
                    };
                });

                return {
                    id: areaNodeId,
                    label: area.area_name,
                    type: 'area',
                    selectable: false,
                    children: [...subAreaNodes, ...workPositionNodes],
                };
            });
        };

        return {
            id: subcompanyNodeId,
            label: subcompany.subcompany_name,
            type: 'subcompany',
            selectable: false,
            children: buildAreaNodes(subcompany.areas ?? [], subcompanyNodeId),
        };
    });
};

// =========================================================================
// Hook consolidado
// =========================================================================

export const useFormAssignPosition = ({ rows, rowId, reload, option, setOption }: Props) => {
    const { t } = useTranslation('workers.form.assign_position');
    const { showAlert } = useRbSystemAlert();
    const canAssignPosition = usePermiso('asignar-puesto');

    const [openForm, setOpenForm] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [openTree, setOpenTree] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState<PositionNodeValue | null>(null);
    const [treeRowId, setTreeRowId] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [treeOrgpath, setTreeOrgpath] = useState<AllTree[]>([]);
    const [employeeOrgpathData, setEmployeeOrgpathData] = useState<ResponseOrgpathEmployee | null>(null);

    const [selectedHiringId, setSelectedHiringId] = useState<number | null>(null);
    const [positionRowsState, setPositionRowsState] = useState<PositionRow[]>([]);
    const [dirty, setDirty] = useState(false);

    const employee = useMemo<Row | null>(() => rows.find((row) => row.id === rowId) ?? null, [rows, rowId]);
    const employeeId = Number(employee?.id ?? 0);

    useEffect(() => {
        if (option !== 'assign_position') return;
        if (!canAssignPosition) {
            setOption(null);
            return;
        }
        setOpenForm(true);
    }, [option, canAssignPosition, setOption]);

    const loadData = useCallback(async () => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const [treeResult, orgpathResult] = await Promise.allSettled([employeeService.getOrgPathTree(), employeeService.getOrgpathByEmployeeId({ employeeId })]);

            if (treeResult.status === 'fulfilled') {
                setTreeOrgpath(treeResult.value.all_tree ?? []);
            } else {
                console.error('[Asignar puesto] Error cargando árbol:', treeResult.reason);
            }

            if (orgpathResult.status === 'fulfilled') {
                const data = orgpathResult.value;
                setEmployeeOrgpathData(data);
            } else {
                console.error('[Asignar puesto] Error cargando contratos:', orgpathResult.reason);
            }
        } catch (error) {
            console.error('[Asignar puesto] Error cargando datos', error);
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useEffect(() => {
        if (openForm && employeeId) {
            loadData();
        }
    }, [openForm, employeeId, loadData]);

    useEffect(() => {
        if (!selectedHiringId || !employeeOrgpathData?.hirings) {
            setPositionRowsState([]);
            return;
        }

        const hiring = employeeOrgpathData.hirings.find((h) => h.hiring_id === selectedHiringId);
        if (!hiring) {
            setPositionRowsState([]);
            return;
        }

        const initialRows: PositionRow[] = (hiring.orgpath ?? []).map((op) => ({
            local_id: genLocalId(),
            hiring_orgpath_id: op.hiring_orgpath_id ?? null,
            to_delete: op.to_delete ?? false,
            subcompany_id: op.subcompany_id,
            subcompany_name: op.subcompany_name,
            area_ids: (op.areas ?? []).map((a) => a.area_id),
            area_path_names: (op.areas ?? []).map((a) => a.area_name),
            work_position_id: op.work_position_id,
            work_position_name: op.work_position_name,
            start_date: parseDateSafe(String(op.start_date)),
            end_date: parseDateSafe(String(op.end_date)),
            editingDates: false,
        }));

        setPositionRowsState(initialRows);
        setDirty(false);
    }, [selectedHiringId, employeeOrgpathData]);

    const closeForm = useCallback(
        (force = false) => {
            if (!force && dirty) {
                setShowMessage(true);
                return;
            }
            setOpenForm(false);
            setShowMessage(false);
            setOpenTree(false);
            setSelectedPosition(null);
            setTreeRowId(null);
            setLoading(false);
            setSaving(false);
            setTreeOrgpath([]);
            setEmployeeOrgpathData(null);
            setSelectedHiringId(null);
            setPositionRowsState([]);
            setDirty(false);
            if (setOption) setOption(null);
        },
        [dirty, setOption],
    );

    const hiringOptions = useMemo(() => {
        return (employeeOrgpathData?.hirings ?? []).map((hiring) => {
            const hiringId = hiring.hiring_id;
            // const activeOrgpaths = (hiring.orgpath ?? []).filter((op) => !op.to_delete);
            // const assignedCount = activeOrgpaths.length;
            // const startDateFormatted = dayjs(hiring.start_date).format('DD/MM/YYYY');
            // const endDateFormatted = dayjs(hiring.end_date).format('DD/MM/YYYY');
            // const range = `${startDateFormatted} - ${endDateFormatted}`;
            // const countLabel = `${assignedCount} ${t(assignedCount === 1 ? 'assign_position.position' : 'assign_position.positions')}`;
            return {
                value: String(hiringId),
                label: `${hiring.name}`,
            };
        });
    }, [employeeOrgpathData, t]);

    const selectedHiringRange = useMemo(() => {
        if (!selectedHiringId || !employeeOrgpathData?.hirings) return null;
        const hiring = employeeOrgpathData.hirings.find((h) => h.hiring_id === selectedHiringId);
        if (!hiring) return null;
        return {
            hiring_id: hiring.hiring_id,
            name: hiring.name,
            start_date: String(hiring.start_date),
            end_date: String(hiring.end_date),
        };
    }, [selectedHiringId, employeeOrgpathData]);

    const activeRows = useMemo(() => positionRowsState.filter((r) => !r.to_delete), [positionRowsState]);

    const contractGaps = useMemo(() => {
        if (!selectedHiringRange?.start_date || !selectedHiringRange?.end_date) return [];
        const contractStart = parseDateSafe(selectedHiringRange.start_date);
        const contractEnd = parseDateSafe(selectedHiringRange.end_date);
        if (!contractStart?.isValid() || !contractEnd?.isValid()) return [];

        const intervals = activeRows.filter((r) => r.start_date?.isValid() && r.end_date?.isValid()).map((r) => ({ start: r.start_date!, end: r.end_date! }));

        if (intervals.length === 0) return [{ start: contractStart, end: contractEnd }];

        return computeContractGaps(mergeIntervals(intervals), contractStart, contractEnd);
    }, [selectedHiringRange, activeRows]);

    const isContractCovered = useMemo(() => activeRows.length > 0 && contractGaps.length === 0, [activeRows, contractGaps]);

    const coverageError = selectedHiringId && activeRows.length > 0 && !isContractCovered ? t('assign_position.alerts.incomplete_coverage.message') : null;

    const { validationErrors, rowErrorsMap } = useMemo(() => {
        const errors: string[] = [];
        const rowErrors = new Map<string, string[]>();
        const addRowError = (localId: string, message: string) => {
            rowErrors.set(localId, [...(rowErrors.get(localId) ?? []), message]);
        };

        if (!selectedHiringId) {
            return { validationErrors: errors, rowErrorsMap: rowErrors };
        }

        if (activeRows.length === 0) {
            errors.push('Debe existir al menos una asignación activa.');
            return { validationErrors: errors, rowErrorsMap: rowErrors };
        }

        const contractStart = selectedHiringRange?.start_date ? parseDateSafe(selectedHiringRange.start_date) : null;
        const contractEnd = selectedHiringRange?.end_date ? parseDateSafe(selectedHiringRange.end_date) : null;

        // 3. Datos obligatorios
        for (const r of activeRows) {
            if (!r.subcompany_id || !r.work_position_id || !r.area_ids || r.area_ids.length === 0) {
                errors.push(`El puesto "${r.work_position_name || 'Seleccionado'}" no tiene área o estructura completa.`);
                addRowError(r.local_id, 'Estructura incompleta');
            }
            if (!r.start_date?.isValid() || !r.end_date?.isValid()) {
                errors.push(`El puesto "${r.work_position_name}" debe tener fechas válidas.`);
                addRowError(r.local_id, 'Fechas inválidas');
            }
        }

        // 4. Orden de fechas
        for (const r of activeRows) {
            if (r.start_date?.isValid() && r.end_date?.isValid() && r.end_date.isBefore(r.start_date, 'day')) {
                errors.push(`La fecha de término de "${r.work_position_name}" no puede ser anterior a la de inicio.`);
                addRowError(r.local_id, 'Fecha de término anterior al inicio');
            }
        }

        // 5. Fechas dentro del contrato
        if (contractStart?.isValid() && contractEnd?.isValid()) {
            for (const r of activeRows) {
                if (r.start_date?.isValid() && r.start_date.isBefore(contractStart, 'day')) {
                    errors.push(`El inicio de "${r.work_position_name}" (${r.start_date.format('DD/MM/YYYY')}) no puede ser anterior al inicio del contrato (${contractStart.format('DD/MM/YYYY')}).`);
                    addRowError(r.local_id, 'Inicio anterior al inicio del contrato');
                }
                if (r.end_date?.isValid() && r.end_date.isAfter(contractEnd, 'day')) {
                    errors.push(`El término de "${r.work_position_name}" (${r.end_date.format('DD/MM/YYYY')}) no puede ser posterior al término del contrato (${contractEnd.format('DD/MM/YYYY')}).`);
                    addRowError(r.local_id, 'Término posterior al término del contrato');
                }
            }
        }

        // 6. Sin solapamientos y 7. Continuidad
        const validSorted = [...activeRows].filter((r) => r.start_date?.isValid() && r.end_date?.isValid()).sort((a, b) => a.start_date!.diff(b.start_date!));
        const repeatedPositionRowIds = new Set(getRepeatedPositionRowIds(positionRowsState));

        for (let i = 0; i < validSorted.length; i++) {
            const curr = validSorted[i];
            if (i > 0) {
                const prev = validSorted[i - 1];
                if (!curr.start_date!.isAfter(prev.end_date!, 'day')) {
                    errors.push(`El puesto "${curr.work_position_name}" se solapa con "${prev.work_position_name}".`);
                    addRowError(curr.local_id, `Fechas solapadas con "${prev.work_position_name}"`);
                } else {
                    const expectedStart = prev.end_date!.add(1, 'day');
                    if (!curr.start_date!.isSame(expectedStart, 'day')) {
                        errors.push(`Falta continuidad: "${curr.work_position_name}" debe iniciar el ${expectedStart.format('DD/MM/YYYY')} (día posterior a "${prev.work_position_name}").`);
                        addRowError(curr.local_id, 'Inicio no continuo');
                    }
                }
            }
        }

        // 8. Un mismo puesto puede repetirse, pero no en asignaciones consecutivas.
        for (let i = 1; i < validSorted.length; i++) {
            const current = validSorted[i];
            if (repeatedPositionRowIds.has(current.local_id)) {
                errors.push(`El puesto "${current.work_position_name}" no puede repetirse de forma continua.`);
                addRowError(current.local_id, 'Puesto repetido de forma continua');
            }
        }

        return { validationErrors: errors, rowErrorsMap: rowErrors };
    }, [selectedHiringId, activeRows, positionRowsState, selectedHiringRange]);

    const isValid = useMemo(() => {
        return Boolean(selectedHiringId) && activeRows.length > 0 && isContractCovered && validationErrors.length === 0;
    }, [selectedHiringId, activeRows, isContractCovered, validationErrors]);

    const timelineSegments = useMemo(
        () =>
            positionRowsState
                .flatMap((row) =>
                    row.to_delete || !row.start_date || !row.end_date || row.end_date.isBefore(row.start_date, 'day')
                        ? []
                        : [{ id: row.local_id, startDate: row.start_date, endDate: row.end_date, label: row.work_position_name }],
                )
                // Timeline asume segmentos ordenados cronológicamente para calcular su posición.
                .sort((a, b) => a.startDate.diff(b.startDate)),
        [positionRowsState],
    );

    const positionTreeItems = useMemo(() => buildPositionTree(treeOrgpath), [treeOrgpath]);

    const addPosition = useCallback(() => {
        if (!selectedPosition) return;

        setDirty(true);
        if (treeRowId) {
            setPositionRowsState((prev) =>
                prev.map((row) =>
                    row.local_id === treeRowId
                        ? {
                              ...row,
                              subcompany_id: selectedPosition.subcompanyId,
                              subcompany_name: selectedPosition.subcompanyName,
                              area_ids: selectedPosition.areaIds,
                              area_path_names: selectedPosition.areaPathNames,
                              work_position_id: selectedPosition.workPositionId,
                              work_position_name: selectedPosition.workPositionName,
                          }
                        : row,
                ),
            );
        } else {
            const contractStart = selectedHiringRange?.start_date ? parseDateSafe(selectedHiringRange.start_date) : null;
            const contractEnd = selectedHiringRange?.end_date ? parseDateSafe(selectedHiringRange.end_date) : null;

            // Ubica el nuevo puesto en el hueco disponible: antes del que llega al fin del contrato, o después del último.
            let defaultStart: Dayjs | null = contractStart ?? dayjs().startOf('day');
            let defaultEnd: Dayjs | null = contractEnd ?? dayjs().startOf('day');

            if (isContractCovered) {
                defaultStart = dayjs().startOf('day');
                defaultEnd = defaultStart.add(1, 'day');
            }

            const validActiveIntervals = activeRows.filter((r) => r.start_date?.isValid() && r.end_date?.isValid()).map((r) => ({ start: r.start_date!, end: r.end_date! }));

            if (!isContractCovered && validActiveIntervals.length > 0 && contractGaps.length > 0) {
                const merged = mergeIntervals(validActiveIntervals);
                const lastMerged = merged[merged.length - 1];
                const reachesContractEnd = Boolean(contractEnd && !lastMerged.end.isBefore(contractEnd, 'day'));

                const gapBeforeLast = reachesContractEnd ? contractGaps.find((gap) => gap.end.add(1, 'day').isSame(lastMerged.start, 'day')) : undefined;

                if (gapBeforeLast) {
                    // Antes del puesto que llega al fin del contrato: ventana mínima de 1 día pegada a su inicio.
                    defaultEnd = gapBeforeLast.end;
                    defaultStart = gapBeforeLast.end.isAfter(gapBeforeLast.start, 'day') ? gapBeforeLast.end.subtract(1, 'day') : gapBeforeLast.start;
                } else {
                    const targetGap = contractGaps[contractGaps.length - 1];
                    // Después del último puesto: ventana mínima de 1 día pegada a su fin.
                    defaultStart = targetGap.start;
                    defaultEnd = targetGap.start.isBefore(targetGap.end, 'day') ? targetGap.start.add(1, 'day') : targetGap.end;
                }
            }

            if (defaultStart && defaultEnd && defaultStart.isAfter(defaultEnd, 'day')) {
                defaultEnd = defaultStart;
            }

            const newRow: PositionRow = {
                local_id: genLocalId(),
                hiring_orgpath_id: null,
                to_delete: false,
                subcompany_id: selectedPosition.subcompanyId,
                subcompany_name: selectedPosition.subcompanyName,
                area_ids: selectedPosition.areaIds,
                area_path_names: selectedPosition.areaPathNames,
                work_position_id: selectedPosition.workPositionId,
                work_position_name: selectedPosition.workPositionName,
                start_date: defaultStart,
                end_date: defaultEnd,
                editingDates: false,
            };
            setPositionRowsState((prev) => [...prev, newRow]);
        }

        setOpenTree(false);
        setSelectedPosition(null);
        setTreeRowId(null);
    }, [selectedPosition, treeRowId, selectedHiringRange, activeRows, contractGaps, isContractCovered]);

    const removeRow = useCallback((localId: string) => {
        setDirty(true);
        setPositionRowsState((prev) =>
            prev
                .map((row) => {
                    if (row.local_id !== localId) return row;
                    if (row.hiring_orgpath_id) {
                        return { ...row, to_delete: true };
                    }
                    return null;
                })
                .filter((row): row is PositionRow => row !== null),
        );
    }, []);

    const restoreRow = useCallback((localId: string) => {
        setDirty(true);
        setPositionRowsState((prev) => prev.map((row) => (row.local_id === localId ? { ...row, to_delete: false } : row)));
    }, []);

    const toggleEditDates = useCallback((localId: string) => {
        setPositionRowsState((prev) => prev.map((row) => (row.local_id === localId ? { ...row, editingDates: !row.editingDates } : row)));
    }, []);

    const changeRowDate = useCallback((localId: string, field: 'start_date' | 'end_date', value: Dayjs | null) => {
        setDirty(true);
        setPositionRowsState((prev) => prev.map((row) => (row.local_id === localId ? { ...row, [field]: value } : row)));
    }, []);

    const handleSelectHiringId = useCallback((hiringId: number | null) => {
        setDirty(true);
        setSelectedHiringId(hiringId);
    }, []);

    const save = useCallback(async () => {
        if (!canAssignPosition || !employeeId || !selectedHiringId) return;

        const orgpath = positionRowsState.map((row) => ({
            hiring_orpath_id: row.hiring_orgpath_id ?? null,
            to_delete: row.to_delete,
            subcompany: row.subcompany_id,
            areas: row.area_ids,
            puesto: row.work_position_id,
            start_date: row.start_date?.isValid() ? row.start_date.format('YYYY-MM-DD') : '',
            end_date: row.end_date?.isValid() ? row.end_date.format('YYYY-MM-DD') : '',
        }));

        try {
            setSaving(true);
            await employeeService.assignHiringOrgpath({
                employee_id: employeeId,
                hiring_id: selectedHiringId,
                orgpath,
            });
            showAlert('success', t('assign_position.alerts.success.message'));
            closeForm(true);
            reload();
        } catch (error) {
            console.error('[Asignar puesto] Error guardando', error);
            showAlert('error', t('assign_position.alerts.error.message'));
        } finally {
            setSaving(false);
        }
    }, [canAssignPosition, employeeId, selectedHiringId, positionRowsState, showAlert, t, closeForm, reload]);

    return {
        t,
        title: t('assign_position.title'),
        openForm,
        loading,
        saving,
        showMessage,
        setShowMessage,
        employeeLabel: employee ? `${employee.person_number} - ${employee.fullname}` : '',
        hiringOptions,
        selectedHiringId,
        setSelectedHiringId: handleSelectHiringId,
        selectedHiringRange,
        timelineSegments,
        positionRows: positionRowsState.map((row) => ({
            localId: row.local_id,
            toDelete: row.to_delete,
            subcompanyName: row.subcompany_name,
            areaNames: row.area_path_names,
            workPositionId: row.work_position_id,
            workPositionName: row.work_position_name,
            startDate: row.start_date,
            endDate: row.end_date,
            editingDates: Boolean(row.editingDates),
            errors: rowErrorsMap.get(row.local_id) ?? [],
        })),
        activePositionsCount: activeRows.length,
        isContractCovered,
        coverageError,
        validationErrors,
        isValid,
        positionTreeItems,
        openTree,
        setOpenTree,
        selectedPosition,
        setSelectedPosition,
        treeRowId,
        setTreeRowId,
        addPosition,
        save,
        closeForm,
        toggleEditDates,
        editPosition: (localId: string) => {
            setTreeRowId(localId);
            setSelectedPosition(null);
            setOpenTree(true);
        },
        removePosition: removeRow,
        restorePosition: restoreRow,
        updateDate: (localId: string, field: 'startDate' | 'endDate', value: string) => changeRowDate(localId, field === 'startDate' ? 'start_date' : 'end_date', value ? dayjs(value) : null),
        handlePositionSelectionChange: (_ids: VirtualTreeNodeId[], details: VirtualTreeSelectionChangeDetails<PositionNodeValue>) => setSelectedPosition(details.node?.value ?? null),
    };
};
